import React from "react";
import { Modal, Card, Button, Typography, Divider, Space, Tag } from "antd";
import { useNavigate } from "react-router-dom";
import {
  CheckOutlined,
  StarOutlined,
  PhoneOutlined,
  MailOutlined,
  CrownOutlined,
} from "@ant-design/icons";

const { Title, Text, Paragraph } = Typography;

const PricingModal = ({ visible, onClose }) => {
  const navigate = useNavigate();
  
  const plans = [
    {
      name: "Standard",
      planKey: "standard",
      price: "10,000 FCFA",
      period: "/mois",
      annualPrice: "Ou 100,000 FCFA/an",
      description: "Idéal pour les petites entreprises",
      color: "#173f5f",
      popular: false,
      features: [
        "Accès complet à la plateforme",
        "2 utilisateurs (Admin et gérant)",
        "1 entreprise avec 1 établissement",
        "Formation en ligne (Google Meet/Teams)",
        "Pack vidéo de formation inclus",
      ],
      limitations: [],
    },
    {
      name: "Business",
      planKey: "business",
      price: "20,000 FCFA",
      period: "/mois",
      annualPrice: "Ou 200,000 FCFA/an",
      description: "Pour les entreprises en croissance",
      color: "#52c41a",
      popular: true,
      features: [
        "Accès complet à la plateforme",
        "5 utilisateurs",
        "1 entreprise avec 3 établissements/points de vente",
        "Formation en ligne (Google Meet/Teams)",
        "Pack vidéo de formation inclus",
        "Formation présentielle (Ouaga) en option",
      ],
      limitations: [],
    },
    {
      name: "VIP",
      planKey: "vip",
      price: "35,000 FCFA",
      period: "/mois",
      annualPrice: "Ou 385,000 FCFA/an",
      description: "Pour les entreprises exigeantes",
      color: "#c93033",
      popular: false,
      features: [
        "Accès complet à la plateforme",
        "2 entreprises distinctes",
        "Jusqu'à 10 établissements au total (5 par entreprise)",
        "Formation en ligne (Google Meet/Teams)",
        "Pack vidéo de formation inclus",
        "Formation présentielle (Ouaga) en option",
        "Support prioritaire",
      ],
      limitations: [],
    },
  ];

  const handleContactClick = () => {
    window.open("tel:+22664989099", "_self");
  };

  const handlePlanSelect = (planKey) => {
    onClose(); // Fermer le modal
    navigate(`/payment?plan=${planKey}`); // Rediriger vers la page de paiement
  };

  return (
    <Modal
      title={
        <div style={{ textAlign: "center", padding: "10px 0" }}>
          <Title level={2} style={{ margin: 0, color: "#173f5f" }}>
            <CrownOutlined style={{ marginRight: 8 }} />
            Choisissez votre plan ELSA GESTION
          </Title>
          <Text type="secondary">
            Des solutions adaptées à chaque taille d'entreprise
          </Text>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1000}
      centered
      className="pricing-modal"
    >
      <div style={{ padding: "20px 0" }}>
        {/* Plans de tarification */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "20px",
            marginBottom: "30px",
          }}
        >
          {plans.map((plan, index) => (
            <Card
              key={index}
              style={{
                border: plan.popular
                  ? `2px solid ${plan.color}`
                  : "1px solid #d9d9d9",
                borderRadius: "12px",
                position: "relative",
                height: "fit-content",
              }}
              bodyStyle={{ padding: "24px" }}
            >
              {plan.popular && (
                <div
                  style={{
                    position: "absolute",
                    top: "-10px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    background: plan.color,
                    color: "white",
                    padding: "5px 15px",
                    borderRadius: "15px",
                    fontSize: "12px",
                    fontWeight: "bold",
                  }}
                >
                  <StarOutlined style={{ marginRight: 4 }} />
                  POPULAIRE
                </div>
              )}

              <div style={{ textAlign: "center", marginBottom: "20px" }}>
                <Title level={3} style={{ color: plan.color, margin: 0 }}>
                  {plan.name}
                </Title>
                <Paragraph type="secondary" style={{ margin: "8px 0" }}>
                  {plan.description}
                </Paragraph>
                <div style={{ margin: "15px 0" }}>
                  <Text
                    style={{
                      fontSize: "28px",
                      fontWeight: "bold",
                      color: plan.color,
                    }}
                  >
                    {plan.price}
                  </Text>
                  {plan.period && (
                    <Text type="secondary" style={{ fontSize: "14px" }}>
                      {plan.period}
                    </Text>
                  )}
                  {plan.annualPrice && (
                    <div style={{ marginTop: "5px" }}>
                      <Text type="secondary" style={{ fontSize: "12px" }}>
                        {plan.annualPrice}
                      </Text>
                    </div>
                  )}
                </div>
              </div>

              <Divider />

              <div style={{ marginBottom: "20px" }}>
                <Title level={5}>Fonctionnalités incluses :</Title>
                <Space direction="vertical" style={{ width: "100%" }}>
                  {plan.features.map((feature, idx) => (
                    <div key={idx} style={{ display: "flex", alignItems: "center" }}>
                      <CheckOutlined
                        style={{ color: plan.color, marginRight: 8 }}
                      />
                      <Text>{feature}</Text>
                    </div>
                  ))}
                </Space>

              </div>

              <Button
                type={plan.popular ? "primary" : "default"}
                size="large"
                block
                style={{
                  backgroundColor: plan.popular ? plan.color : undefined,
                  borderColor: plan.color,
                  color: plan.popular ? "white" : plan.color,
                  height: "45px",
                  fontWeight: "bold",
                }}
                onClick={() => handlePlanSelect(plan.planKey)}
              >
                Choisir cette offre
              </Button>
            </Card>
          ))}
        </div>

        {/* Section contact */}
        <Card
          style={{
            background: "linear-gradient(135deg, #0a2740, #173f5f)",
            border: "none",
            borderRadius: "12px",
            color: "white",
          }}
          bodyStyle={{ padding: "30px", textAlign: "center" }}
        >
          <Title level={3} style={{ color: "white", margin: 0 }}>
            Besoin d'aide pour choisir ?
          </Title>
          <Paragraph style={{ color: "white", margin: "15px 0", fontSize: "16px" }}>
            Notre équipe commerciale est là pour vous accompagner et répondre à
            toutes vos questions
          </Paragraph>
          <Space size="large">
            <Button
              type="default"
              size="large"
              icon={<PhoneOutlined />}
              onClick={handleContactClick}
              style={{
                background: "white",
                color: "#173f5f",
                border: "none",
                fontWeight: "bold",
              }}
            >
              +226 64 98 90 99
            </Button>
            <Button
              type="default"
              size="large"
              icon={<MailOutlined />}
              onClick={() => window.open("mailto:info@elsa-technologies.com")}
              style={{
                background: "rgba(255,255,255,0.2)",
                color: "white",
                border: "1px solid white",
                fontWeight: "bold",
              }}
            >
              info@elsa-technologies.com
            </Button>
          </Space>
        </Card>

        {/* Garanties et informations */}
        <div style={{ textAlign: "center", marginTop: "20px" }}>
          <Space size="large" wrap>
            <Tag color="green">✓ Essai gratuit 14 jours</Tag>
            <Tag color="blue">✓ Formation incluse</Tag>
            <Tag color="orange">✓ Support inclus</Tag>
            <Tag color="purple">✓ Tous les prix incluent les taxes</Tag>
          </Space>
          <div style={{ marginTop: "15px", padding: "15px", backgroundColor: "#f0f9ff", borderRadius: "8px" }}>
            <Text style={{ color: "#173f5f", fontSize: "14px" }}>
              📍 Tous les prix sont en FCFA et incluent toutes les taxes applicables
            </Text>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default PricingModal;
